/**
 * DashboardKpiSection — the four-tile KPI grid at the top of the
 * authenticated Hub dashboard (HD-95).
 *
 * Replaces the four hardcoded `<Kpi>` calls in `routes/Dashboard.tsx`.
 * Wires `useDashboardKpis()` for data, renders skeletons while loading
 * (after a 200ms delay to suppress flicker on cache-fresh responses),
 * and shows an inline retry chip on error.
 *
 * Free users get the bundle with zero values plus an "Upgrade for
 * analytics" pill anchored to the section header.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  useDashboardKpis,
  type KpiBundle,
  type ScansTile,
  type DevicesTile,
  type ActiveCodesTile,
  type TopCountryTile,
} from "../lib/useDashboardKpis.js";

// ---------------------------------------------------------------------------
// Tokens (matches Dashboard.tsx so the section feels native).
// ---------------------------------------------------------------------------

const T = {
  purple: "#351DBD",
  purpleLight: "#EDE9FC",
  orange: "#FF861D",
  blue: "#349DFF",
  navy: "#261F59",
  textMuted: "#6b7084",
  textFaint: "#9096a8",
  muted: "#E3EBF6",
  white: "#ffffff",
  successText: "#166534",
  bgTint: "#F7F6FB",
  shadow: {
    sm: "0 1px 3px rgba(53,29,189,0.04), 0 2px 8px rgba(53,29,189,0.04)",
  },
} as const;

// ---------------------------------------------------------------------------
// Skeleton — shown while data is loading (>200ms).
// ---------------------------------------------------------------------------

function KpiSkeleton({ accent }: { accent: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-busy="true"
      aria-label={t("kpi.loadingKpi")}
      style={{
        background: T.white,
        border: `1px solid ${T.muted}`,
        borderRadius: 16,
        padding: "18px 20px",
        boxShadow: T.shadow.sm,
        minHeight: 108,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ height: 11, width: 80, borderRadius: 4, background: T.bgTint }} />
      <div style={{ height: 28, width: 120, borderRadius: 6, background: T.muted }} />
      <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between" }}>
        <div style={{ height: 11, width: 60, borderRadius: 4, background: T.bgTint }} />
        <div style={{ height: 18, width: 64, borderRadius: 4, background: accent, opacity: 0.18 }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spark — re-implementation of the existing one (identical shape).
// ---------------------------------------------------------------------------

function Spark({ data, color }: { data: number[]; color: string }) {
  const w = 64, h = 22;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const rng = Math.max(1, max - min);
  const pts = data
    .map((v, i) => {
      const x = (i / Math.max(1, data.length - 1)) * w;
      const y = h - ((v - min) / rng) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ overflow: "visible" }}
      aria-hidden="true"
    >
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={w}
        cy={h - ((data[data.length - 1] - min) / rng) * h}
        r="2.5"
        fill={color}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// KPI tile — one card.
// ---------------------------------------------------------------------------

interface TileProps {
  label: string;
  value: string;
  delta: string;
  trendUp?: boolean;
  sparkline: number[] | null;
  accent: string;
  big?: boolean;
}

function Tile({ label, value, delta, trendUp, sparkline, accent, big }: TileProps) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t("kpi.tileAria", { label })}
      style={{
        background: T.white,
        border: `1px solid ${T.muted}`,
        borderRadius: 16,
        padding: "18px 20px",
        boxShadow: T.shadow.sm,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minHeight: 108,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
        <span style={{ fontSize: big ? 26 : 30, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1 }}>
          {value}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: "auto" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: trendUp ? T.successText : T.textMuted }}>{delta}</span>
        {sparkline && <Spark data={sparkline} color={accent} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile mappers — bundle → display props.
// ---------------------------------------------------------------------------

const numberFmt = new Intl.NumberFormat("en-US");

function scansToTileProps(scans: ScansTile, accent: string, t: TFunction): TileProps {
  const delta = scans.delta;
  const deltaText =
    delta === null
      ? "—"
      : delta.direction === "up"
      ? `+${delta.percentage}%`
      : delta.direction === "down"
      ? `−${delta.percentage}%`
      : t("kpi.noChange");
  return {
    label: t("kpi.scansLabel"),
    value: numberFmt.format(scans.value),
    delta: deltaText,
    trendUp: delta?.direction === "up",
    sparkline: scans.sparkline,
    accent,
  };
}

function devicesToTileProps(devices: DevicesTile, accent: string, t: TFunction): TileProps {
  if (devices.value === 0) {
    return { label: t("kpi.devicesLabel"), value: "0", delta: t("kpi.noScansYet"), sparkline: null, accent };
  }
  const top = devices.breakdown[0];
  const restCount = devices.breakdown.slice(1).reduce((a, b) => a + b.count, 0);
  const sub = restCount > 0 ? t("kpi.devicesBreakdown", { label: top.label, count: top.count, rest: restCount }) : t("kpi.devicesMostly", { label: top.label.toLowerCase() });
  return {
    label: t("kpi.devicesLabel"),
    value: numberFmt.format(devices.value),
    delta: sub,
    sparkline: null,
    accent,
  };
}

function activeCodesToTileProps(active: ActiveCodesTile, accent: string, t: TFunction): TileProps {
  const free =
    active.quota === null
      ? t("kpi.unlimited")
      : t("kpi.slotsFree", { count: Math.max(0, active.quota - active.value) });
  return {
    label: t("kpi.activeCodesLabel"),
    value: active.label,
    delta: free,
    sparkline: null,
    accent,
  };
}

function topCountryToTileProps(country: TopCountryTile | null, accent: string, t: TFunction): TileProps {
  if (!country) {
    return { label: t("kpi.topCountryLabel"), value: "—", delta: t("kpi.noScansYet"), sparkline: null, accent, big: true };
  }
  return {
    label: t("kpi.topCountryLabel"),
    value: `${country.flag} ${country.code}`,
    delta: t("kpi.percentOfScans", { percentage: country.percentage }),
    sparkline: null,
    accent,
    big: true,
  };
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function DashboardKpiSection() {
  const { data, state, error, reload } = useDashboardKpis();

  const { t } = useTranslation();

  // Suppress skeleton flicker for fast cache hits (HD-95 critique #11).
  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (state !== "loading") {
      setShowSkeleton(false);
      return;
    }
    const t = setTimeout(() => setShowSkeleton(true), 200);
    return () => clearTimeout(t);
  }, [state]);

  if (state === "loading" || state === "idle") {
    if (!showSkeleton && state === "loading") {
      // Render the section invisibly to reserve layout but avoid flicker.
      return <Section reservedOnly />;
    }
    return (
      <Section>
        <KpiSkeleton accent={T.blue} />
        <KpiSkeleton accent={T.purple} />
        <KpiSkeleton accent={T.orange} />
        <KpiSkeleton accent={T.navy} />
      </Section>
    );
  }

  if (state === "error" || !data) {
    return (
      <Section>
        <ErrorTile onRetry={reload} message={error?.message ?? t("kpi.couldntLoadMetrics")} accent={T.blue} label={t("kpi.scansLabel")} />
        <ErrorTile onRetry={reload} message={error?.message ?? t("kpi.couldntLoadMetrics")} accent={T.purple} label={t("kpi.devicesLabel")} />
        <ErrorTile onRetry={reload} message={error?.message ?? t("kpi.couldntLoadMetrics")} accent={T.orange} label={t("kpi.activeCodesLabel")} />
        <ErrorTile onRetry={reload} message={error?.message ?? t("kpi.couldntLoadMetrics")} accent={T.navy} label={t("kpi.topCountryLabel")} />
      </Section>
    );
  }

  return (
    <SectionWithUpgrade upgradeRequired={data.upgradeRequired === true}>
      <Tile {...scansToTileProps(data.tiles.scans, T.blue, t)} />
      <Tile {...devicesToTileProps(data.tiles.devices, T.purple, t)} />
      <Tile {...activeCodesToTileProps(data.tiles.activeCodes, T.orange, t)} />
      <Tile {...topCountryToTileProps(data.tiles.topCountry, T.navy, t)} />
    </SectionWithUpgrade>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function Section({
  children,
  reservedOnly,
}: {
  children?: React.ReactNode;
  reservedOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 16,
        marginBottom: 32,
        visibility: reservedOnly ? "hidden" : undefined,
        minHeight: 108,
      }}
      aria-label={t("kpi.dashboardKpis")}
    >
      {children}
    </section>
  );
}

function SectionWithUpgrade({
  children,
  upgradeRequired,
}: {
  children: React.ReactNode;
  upgradeRequired: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: 32 }}>
      {upgradeRequired && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: T.purpleLight,
            color: T.purple,
            border: `1px solid ${T.muted}`,
            padding: "6px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          <span aria-hidden>🔒</span>
          {t("kpi.analyticsProFeature")}
          <Link
            to="/pricing"
            style={{
              color: T.purple,
              fontWeight: 700,
              textDecoration: "underline",
            }}
          >
            {t("kpi.upgradeForAnalytics")}
          </Link>
        </div>
      )}
      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
        }}
        aria-label={t("kpi.dashboardKpis")}
      >
        {children}
      </section>
    </div>
  );
}

function ErrorTile({
  label,
  message,
  onRetry,
  accent,
}: {
  label: string;
  message: string;
  onRetry: () => void;
  accent: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t("kpi.errorTileAria", { label })}
      style={{
        background: T.white,
        border: `1px solid ${T.muted}`,
        borderRadius: 16,
        padding: "18px 20px",
        boxShadow: T.shadow.sm,
        minHeight: 108,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>—</div>
      <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }} title={message}>
          {message.length > 28 ? t("kpi.couldntLoad") : message}
        </span>
        <button
          type="button"
          onClick={onRetry}
          style={{
            background: T.bgTint,
            border: `1px solid ${T.muted}`,
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 600,
            color: accent,
            cursor: "pointer",
          }}
        >
          {t("kpi.retry")}
        </button>
      </div>
    </div>
  );
}
